/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON boundary */
import { supabase } from "@/integrations/supabase/client";
import { createDocument as createCorpusDocument, type CorpusBucket } from "@/lib/workspaceCorpus";
export type { CorpusBucket };

export type ArchiveFolder = {
  id: string;
  workspace_id: string;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

export type ArchivedPiece = {
  id: string;
  workspace_id: string;
  title: string | null;
  content_type: string | null;
  status: string | null;
  archive_folder_id: string | null;
  updated_at: string;
  publication_count: number;
  first_published_at: string | null;
  destination_formats: string[];
};

const foldersTable = () => (supabase as any).from("composer_archive_folders");
const piecesTable = () => (supabase as any).from("content_pieces");

export const listArchiveFolders = async (workspaceId: string): Promise<ArchiveFolder[]> => {
  const { data, error } = await foldersTable()
    .select("*")
    .eq("workspace_id", workspaceId)
    .order("sort_order", { ascending: true })
    .order("created_at", { ascending: true });
  if (error) throw error;
  return (data ?? []) as ArchiveFolder[];
};

export const createArchiveFolder = async (workspaceId: string, name: string): Promise<ArchiveFolder> => {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("Folder name is required");
  const { data, error } = await foldersTable()
    .insert({ workspace_id: workspaceId, name: trimmed, sort_order: Date.now() })
    .select("*")
    .single();
  if (error) throw error;
  return data as ArchiveFolder;
};

export const renameArchiveFolder = async (id: string, name: string): Promise<void> => {
  const trimmed = name.trim();
  if (!trimmed) throw new Error("Folder name is required");
  const { error } = await foldersTable().update({ name: trimmed }).eq("id", id);
  if (error) throw error;
};

export const deleteArchiveFolder = async (id: string): Promise<void> => {
  const { error } = await foldersTable().delete().eq("id", id);
  if (error) throw error;
};

export const assignPieceToFolder = async (
  pieceId: string,
  folderId: string | null,
): Promise<void> => {
  const { error } = await piecesTable()
    .update({ archive_folder_id: folderId })
    .eq("id", pieceId);
  if (error) throw error;
};

export const reorderArchiveFolders = async (orderedIds: string[]): Promise<void> => {
  // Sort_order is a number; spread as 1, 2, 3 …
  const updates = orderedIds.map((id, idx) =>
    foldersTable().update({ sort_order: (idx + 1) * 100 }).eq("id", id),
  );
  const results = await Promise.all(updates);
  for (const r of results) {
    if ((r as any).error) throw (r as any).error;
  }
};

export const bulkAssignPiecesToFolder = async (
  pieceIds: string[],
  folderId: string | null,
): Promise<void> => {
  if (pieceIds.length === 0) return;
  const { error } = await piecesTable()
    .update({ archive_folder_id: folderId })
    .in("id", pieceIds);
  if (error) throw error;
};

/** Returns the subset of `pieceIds` whose latest draft body or title matches `query`. */
export const searchArchivePieceContents = async (
  workspaceId: string,
  pieceIds: string[],
  query: string,
): Promise<Set<string>> => {
  const q = query.trim().toLowerCase();
  if (!q || pieceIds.length === 0) return new Set();
  const { data, error } = await (supabase as any)
    .from("content_piece_drafts")
    .select("piece_id, draft_body, draft_doc, draft_title")
    .in("piece_id", pieceIds)
    .order("updated_at", { ascending: false });
  if (error) throw error;
  const matches = new Set<string>();
  for (const d of (data ?? []) as any[]) {
    if (matches.has(d.piece_id)) continue;
    const title = (d.draft_title ?? "").toLowerCase();
    const body = (d.draft_body ?? "").toLowerCase();
    const docText = d.draft_doc ? briefDocToPlainText(d.draft_doc).toLowerCase() : "";
    if (title.includes(q) || body.includes(q) || docText.includes(q)) {
      matches.add(d.piece_id);
    }
  }
  return matches;
};

export const listArchivedPieces = async (workspaceId: string): Promise<ArchivedPiece[]> => {
  // Pull all pieces that have at least one publication.
  const { data: pieces, error: piecesError } = await piecesTable()
    .select("id, workspace_id, title, content_type, status, archive_folder_id, updated_at")
    .eq("workspace_id", workspaceId)
    .order("updated_at", { ascending: false });
  if (piecesError) throw piecesError;

  const ids = (pieces ?? []).map((p: any) => p.id);
  if (ids.length === 0) return [];

  const { data: pubs, error: pubsError } = await (supabase as any)
    .from("content_piece_publications")
    .select("piece_id, destination_format, completed_at, created_at")
    .in("piece_id", ids);
  if (pubsError) throw pubsError;

  const byPiece = new Map<string, { count: number; first: string | null; formats: Set<string> }>();
  for (const pub of (pubs ?? []) as any[]) {
    const entry = byPiece.get(pub.piece_id) ?? { count: 0, first: null, formats: new Set<string>() };
    entry.count += 1;
    const t = pub.completed_at ?? pub.created_at;
    if (t && (!entry.first || new Date(t) < new Date(entry.first))) entry.first = t;
    if (pub.destination_format) entry.formats.add(pub.destination_format);
    byPiece.set(pub.piece_id, entry);
  }

  return (pieces ?? [])
    .filter((p: any) => byPiece.has(p.id))
    .map((p: any): ArchivedPiece => {
      const e = byPiece.get(p.id)!;
      return {
        id: p.id,
        workspace_id: p.workspace_id,
        title: p.title,
        content_type: p.content_type,
        status: p.status,
        archive_folder_id: p.archive_folder_id,
        updated_at: p.updated_at,
        publication_count: e.count,
        first_published_at: e.first,
        destination_formats: Array.from(e.formats).sort(),
      };
    });
};

// ---------------- Promote to Corpus ----------------


const briefDocToPlainText = (raw: unknown): string => {
  if (!raw || typeof raw !== "object") return "";
  const out: string[] = [];
  const walk = (node: any): void => {
    if (!node || typeof node !== "object") return;
    if (node.type === "text" && typeof node.text === "string") {
      out.push(node.text);
      return;
    }
    if (Array.isArray(node.content)) {
      for (const child of node.content) walk(child);
      // Block-level separators
      if (
        node.type === "paragraph" ||
        node.type === "heading" ||
        node.type === "blockquote" ||
        node.type === "listItem"
      ) {
        out.push("\n\n");
      }
    }
  };
  walk(raw as any);
  return out.join("").replace(/\n{3,}/g, "\n\n").trim();
};

export type PromoteResult = { documentId: string };

export const promotePieceToCorpus = async (args: {
  workspaceId: string;
  pieceId: string;
  bucket: CorpusBucket;
  /** When provided, the promoted document is placed in this Corpus folder
   *  (which must belong to the same bucket). Otherwise we fall back to a
   *  per-bucket "From Archive" folder, creating it on first use. */
  targetFolderId?: string | null;
}): Promise<PromoteResult> => {
  const { workspaceId, pieceId, bucket, targetFolderId } = args;

  // Fetch latest user_edit revision; fall back to most recent draft.
  const { data: drafts, error: draftsError } = await (supabase as any)
    .from("content_piece_drafts")
    .select("id, revision_kind, draft_doc, draft_body, draft_title, updated_at, created_at")
    .eq("piece_id", pieceId)
    .order("updated_at", { ascending: false })
    .limit(20);
  if (draftsError) throw draftsError;

  const list = (drafts ?? []) as any[];
  const userEdit = list.find((d) => d.revision_kind === "user_edit" && d.draft_doc);
  const ai = list.find((d) => d.draft_body);

  let body = "";
  let title: string | null = null;
  if (userEdit) {
    body = briefDocToPlainText(userEdit.draft_doc);
    title = userEdit.draft_title ?? null;
  } else if (ai) {
    body = ai.draft_body ?? "";
    title = ai.draft_title ?? null;
  }

  // Pull piece title as fallback.
  if (!title) {
    const { data: piece } = await piecesTable().select("title").eq("id", pieceId).maybeSingle();
    title = (piece as any)?.title ?? "Untitled";
  }

  const doc = await createCorpusDocument({
    workspaceId,
    folderId: null, // assign below so we can validate the bucket
    title: title ?? "Untitled",
    body,
    sourceKind: "manual",
  });

  // Resolve the destination folder.
  let folderId = targetFolderId ?? null;
  if (folderId) {
    const { data: f } = await (supabase as any)
      .from("workspace_corpus_folders")
      .select("id, bucket, workspace_id")
      .eq("id", folderId)
      .maybeSingle();
    if (!f || (f as any).workspace_id !== workspaceId || (f as any).bucket !== bucket) {
      // Silently fall back rather than failing — caller's bucket wins.
      folderId = null;
    }
  }
  if (!folderId) {
    const folderName = "From Archive";
    const { data: existingFolders } = await (supabase as any)
      .from("workspace_corpus_folders")
      .select("id")
      .eq("workspace_id", workspaceId)
      .eq("bucket", bucket)
      .eq("name", folderName)
      .limit(1);
    folderId = (existingFolders?.[0]?.id as string) ?? null;
    if (!folderId) {
      const { data: newFolder, error: folderErr } = await (supabase as any)
        .from("workspace_corpus_folders")
        .insert({ workspace_id: workspaceId, bucket, name: folderName, parent_folder_id: null })
        .select("id")
        .single();
      if (folderErr) throw folderErr;
      folderId = newFolder.id as string;
    }
  }

  await (supabase as any)
    .from("workspace_corpus_documents")
    .update({ folder_id: folderId })
    .eq("id", doc.id);

  return { documentId: doc.id };
};
